import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { login, userDetails } from "../../api/auth_api";
import { useDispatch } from "react-redux";
import { setUserProfile } from "../../redux/store/userSlice";
import { Eye, EyeOff, AlertTriangle, ShieldCheck, X } from "lucide-react";
import { CircularProgress } from "@mui/material";
import CustomLoader from "../../components/common/customLoader";

const CandlestickChartBackground = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let candles = [];
        let maData = []; // Moving average data
        let volumeData = [];
        let signals = [];
        let scrollOffset = 0;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        resize();
        window.addEventListener('resize', resize);

        // Generate candlestick data
        const generateCandle = (prevClose) => {
            const open = prevClose + (Math.random() - 0.5) * 15;
            const close = open + (Math.random() - 0.5) * 20;
            const high = Math.max(open, close) + Math.random() * 10;
            const low = Math.min(open, close) - Math.random() * 10;
            const volume = Math.random() * 50 + 20;

            return { open, high, low, close, volume };
        };

        // Initialize candles
        let lastClose = 300;
        const candleCount = Math.ceil(canvas.width / 25) + 10;

        for (let i = 0; i < candleCount; i++) {
            const candle = generateCandle(lastClose);
            candles.push(candle);
            volumeData.push(candle.volume);
            lastClose = candle.close;

            // Calculate moving average (simple 20-period MA)
            if (i >= 19) {
                const sum = candles.slice(i - 19, i + 1).reduce((acc, c) => acc + c.close, 0);
                maData.push(sum / 20);
            } else {
                maData.push(null);
            }

            // Generate random buy/sell signals
            if (i > 20 && Math.random() > 0.92) {
                signals.push({
                    index: i,
                    type: Math.random() > 0.5 ? 'buy' : 'sell',
                    price: candle.close
                });
            }
        }

        const drawCandle = (x, candle, width) => {
            const bullish = candle.close > candle.open;
            const bodyTop = Math.min(candle.open, candle.close);
            const bodyBottom = Math.max(candle.open, candle.close);
            const bodyHeight = Math.abs(candle.close - candle.open);

            // Wick (high-low line)
            ctx.strokeStyle = bullish ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x + width / 2, candle.high);
            ctx.lineTo(x + width / 2, candle.low);
            ctx.stroke();

            // Body
            ctx.fillStyle = bullish ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)';
            ctx.fillRect(x, bodyTop, width - 2, Math.max(bodyHeight, 2));
        };

        const drawVolume = (x, volume, width, maxVolume) => {
            const volumeHeight = (volume / maxVolume) * 80;
            ctx.fillStyle = 'rgba(59, 130, 246, 0.3)';
            ctx.fillRect(x, canvas.height - volumeHeight - 10, width - 2, volumeHeight);
        };

        const drawSignal = (x, y, type) => {
            ctx.save();
            ctx.fillStyle = type === 'buy' ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)';
            ctx.strokeStyle = type === 'buy' ? 'rgba(34, 197, 94, 1)' : 'rgba(239, 68, 68, 1)';
            ctx.lineWidth = 2;

            // Draw triangle
            ctx.beginPath();
            if (type === 'buy') {
                ctx.moveTo(x, y + 15);
                ctx.lineTo(x - 8, y + 5);
                ctx.lineTo(x + 8, y + 5);
            } else {
                ctx.moveTo(x, y - 15);
                ctx.lineTo(x - 8, y - 5);
                ctx.lineTo(x + 8, y - 5);
            }
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

            // Label
            ctx.fillStyle = 'white';
            ctx.font = 'bold 10px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(type.toUpperCase(), x, y + (type === 'buy' ? 30 : -20));
            ctx.restore();
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const candleWidth = 20;
            const spacing = 25;
            const chartHeight = canvas.height - 120;

            // Find min/max for scaling
            const visibleCandles = candles.slice(0, candleCount);
            const allPrices = visibleCandles.flatMap(c => [c.high, c.low]);
            const minPrice = Math.min(...allPrices) - 20;
            const maxPrice = Math.max(...allPrices) + 20;
            const priceRange = maxPrice - minPrice;

            const scaleY = (price) => {
                return 40 + ((maxPrice - price) / priceRange) * (chartHeight - 80);
            };

            const maxVolume = Math.max(...volumeData);

            // Draw grid lines
            ctx.strokeStyle = 'rgba(100, 116, 139, 0.1)';
            ctx.lineWidth = 1;
            for (let i = 0; i < 5; i++) {
                const y = 40 + (chartHeight - 80) * (i / 4);
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            }

            // Draw moving average
            ctx.strokeStyle = 'rgba(234, 179, 8, 0.6)';
            ctx.lineWidth = 2;
            ctx.beginPath();
            let maStarted = false;

            candles.forEach((candle, i) => {
                if (maData[i] !== null) {
                    const x = (i * spacing - scrollOffset) + candleWidth / 2;
                    const y = scaleY(maData[i]);

                    if (x > -spacing && x < canvas.width + spacing) {
                        if (!maStarted) {
                            ctx.moveTo(x, y);
                            maStarted = true;
                        } else {
                            ctx.lineTo(x, y);
                        }
                    }
                }
            });
            ctx.stroke();

            // Draw candles and volumes
            candles.forEach((candle, i) => {
                const x = i * spacing - scrollOffset;

                if (x > -spacing && x < canvas.width + spacing) {
                    // Scale candle prices
                    const scaledCandle = {
                        open: scaleY(candle.open),
                        high: scaleY(candle.high),
                        low: scaleY(candle.low),
                        close: scaleY(candle.close)
                    };

                    drawCandle(x, scaledCandle, candleWidth);
                    drawVolume(x, volumeData[i], candleWidth, maxVolume);
                }
            });

            // Draw signals
            signals.forEach(signal => {
                const x = signal.index * spacing - scrollOffset + candleWidth / 2;
                const y = scaleY(signal.price);

                if (x > 0 && x < canvas.width) {
                    drawSignal(x, y, signal.type);
                }
            });

            // Scroll and generate new candles
            scrollOffset += 0.5;

            if (scrollOffset >= spacing) {
                scrollOffset -= spacing;

                // Remove old candle and add new one
                candles.shift();
                maData.shift();
                volumeData.shift();
                signals = signals.map(s => ({ ...s, index: s.index - 1 })).filter(s => s.index >= 0);

                const newCandle = generateCandle(candles[candles.length - 1].close);
                candles.push(newCandle);
                volumeData.push(newCandle.volume);

                // Calculate new MA
                const sum = candles.slice(-20).reduce((acc, c) => acc + c.close, 0);
                maData.push(sum / 20);

                // Maybe add new signal
                if (Math.random() > 0.95) {
                    signals.push({
                        index: candles.length - 1,
                        type: Math.random() > 0.5 ? 'buy' : 'sell',
                        price: newCandle.close
                    });
                }
            }

            // Draw legend
            ctx.fillStyle = 'rgba(30, 41, 59, 0.8)';
            ctx.fillRect(10, 10, 250, 60);
            ctx.strokeStyle = 'rgba(100, 116, 139, 0.5)';
            ctx.strokeRect(10, 10, 250, 60);

            ctx.font = '12px Arial';
            ctx.fillStyle = 'rgba(148, 163, 184, 1)';
            ctx.textAlign = 'left';
            ctx.fillText('Live Market Simulation', 20, 30);

            ctx.fillStyle = 'rgba(34, 197, 94, 1)';
            ctx.fillText('● Green = Bullish', 20, 50);
            ctx.fillStyle = 'rgba(239, 68, 68, 1)';
            ctx.fillText('● Red = Bearish', 150, 50);

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            style={{ opacity: 0.5 }}
        />
    );
};

/* ─────────────────────────────────────────────
   Stock Recommendation Disclaimer Popup
   Shows only on first login (tracked via localStorage)
   ───────────────────────────────────────────── */
const DisclaimerPopup = ({ onAccept, onDecline }) => {
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const contentRef = useRef(null);

    const handleScroll = () => {
        const el = contentRef.current;
        if (!el) return;
        const threshold = 20;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
            setHasScrolledToBottom(true);
        }
    };

    // Check on mount if content is short enough to not need scrolling
    useEffect(() => {
        const el = contentRef.current;
        if (el && el.scrollHeight <= el.clientHeight) {
            setHasScrolledToBottom(true);
        }
    }, []);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)' }}
        >
            <div
                className="relative w-full max-w-lg"
                style={{
                    animation: 'disclaimerSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
            >
                <style>{`
                    @keyframes disclaimerSlideIn {
                        from { opacity: 0; transform: translateY(24px) scale(0.97); }
                        to { opacity: 1; transform: translateY(0) scale(1); }
                    }
                    .disclaimer-scrollbar::-webkit-scrollbar {
                        width: 6px;
                    }
                    .disclaimer-scrollbar::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    .disclaimer-scrollbar::-webkit-scrollbar-thumb {
                        background: rgba(100, 116, 139, 0.4);
                        border-radius: 3px;
                    }
                    .disclaimer-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: rgba(100, 116, 139, 0.6);
                    }
                `}</style>

                <div
                    className="rounded-2xl overflow-hidden"
                    style={{
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border-primary)',
                        boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5)',
                    }}
                >
                    {/* Header */}
                    <div
                        className="px-6 py-5 flex items-center gap-3"
                        style={{
                            borderBottom: '1px solid var(--color-border-primary)',
                            background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.08), rgba(239, 68, 68, 0.05))',
                        }}
                    >
                        <div
                            className="flex items-center justify-center w-10 h-10 rounded-full"
                            style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)' }}
                        >
                            <AlertTriangle size={20} style={{ color: 'rgb(234, 179, 8)' }} />
                        </div>
                        <div>
                            <h2
                                className="font-semibold text-lg"
                                style={{ color: 'var(--color-text-primary)' }}
                            >
                                Important Disclaimer
                            </h2>
                            <p
                                className="text-xs"
                                style={{ color: 'var(--color-text-muted)' }}
                            >
                                Stock Recommendation Disclosure
                            </p>
                        </div>
                    </div>

                    {/* Scrollable Content */}
                    <div
                        ref={contentRef}
                        onScroll={handleScroll}
                        className="px-6 py-5 space-y-4 overflow-y-auto disclaimer-scrollbar"
                        style={{
                            maxHeight: '340px',
                            color: 'var(--color-text-secondary)',
                            fontSize: '14px',
                            lineHeight: '1.7',
                        }}
                    >
                        <p>
                            <strong style={{ color: 'var(--color-text-primary)' }}>
                                Please read this disclaimer carefully before proceeding.
                            </strong>
                        </p>

                        <p>
                            The stock recommendations, analysis, and market insights provided on
                            FactorAlpha are for <strong style={{ color: 'var(--color-text-primary)' }}>
                                informational and educational purposes only</strong> and should not be
                            construed as financial advice, investment advice, trading advice, or any
                            other type of advice.
                        </p>

                        <div
                            className="p-3 rounded-lg"
                            style={{
                                backgroundColor: 'rgba(234, 179, 8, 0.08)',
                                border: '1px solid rgba(234, 179, 8, 0.2)',
                                borderRadius: 'var(--radius-lg)',
                            }}
                        >
                            <p className="font-medium" style={{ color: 'rgb(234, 179, 8)', marginBottom: '6px', fontSize: '13px' }}>
                                ⚠ Key Points to Understand:
                            </p>
                            <ul style={{ paddingLeft: '18px', fontSize: '13px', margin: 0 }}>
                                <li style={{ marginBottom: '4px' }}>
                                    Past performance does not guarantee future results.
                                </li>
                                <li style={{ marginBottom: '4px' }}>
                                    All investments involve risk, including the potential loss of principal.
                                </li>
                                <li style={{ marginBottom: '4px' }}>
                                    Recommendations are generated using algorithms and models that may not account for your individual financial situation.
                                </li>
                                <li>
                                    You should consult with a qualified financial advisor before making any investment decisions.
                                </li>
                            </ul>
                        </div>

                        <p>
                            FactorAlpha, its affiliates, and contributors are <strong style={{ color: 'var(--color-text-primary)' }}>
                                not registered investment advisors, broker-dealers, or financial planners</strong>.
                            We do not guarantee the accuracy, completeness, or timeliness of any
                            information provided.
                        </p>

                        <p>
                            By proceeding, you acknowledge that you are solely responsible for your
                            own investment decisions and that you have read and understood this disclaimer
                            in its entirety.
                        </p>
                    </div>

                    {/* Scroll hint (shown if user hasn't scrolled to bottom) */}
                    {!hasScrolledToBottom && (
                        <div
                            className="text-center py-2"
                            style={{
                                color: 'var(--color-text-muted)',
                                fontSize: '12px',
                                borderTop: '1px solid var(--color-border-primary)',
                                background: 'linear-gradient(to top, var(--color-surface) 60%, transparent)',
                            }}
                        >
                            ↓ Scroll down to read the full disclaimer
                        </div>
                    )}

                    {/* Footer with checkbox + buttons */}
                    <div
                        className="px-6 py-4"
                        style={{ borderTop: '1px solid var(--color-border-primary)' }}
                    >
                        {/* Checkbox */}
                        <label
                            className="flex items-start gap-3 cursor-pointer mb-4"
                            style={{ fontSize: '13px' }}
                        >
                            <input
                                type="checkbox"
                                checked={accepted}
                                onChange={(e) => setAccepted(e.target.checked)}
                                className="mt-0.5"
                                style={{
                                    accentColor: 'var(--color-primary)',
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                }}
                            />
                            <span style={{ color: 'var(--color-text-secondary)' }}>
                                I have read and understood the disclaimer. I acknowledge that stock
                                recommendations are not financial advice and I am solely responsible
                                for my investment decisions.
                            </span>
                        </label>

                        {/* Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={onDecline}
                                className="flex-1 py-2.5 rounded-lg font-medium transition-all"
                                style={{
                                    backgroundColor: 'transparent',
                                    border: '1px solid var(--color-border-primary)',
                                    color: 'var(--color-text-secondary)',
                                    borderRadius: 'var(--radius-lg)',
                                    cursor: 'pointer',
                                    transition: 'var(--transition-normal)',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                                    e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                                    e.currentTarget.style.color = 'var(--color-error)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.borderColor = 'var(--color-border-primary)';
                                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                                }}
                            >
                                Decline
                            </button>
                            <button
                                onClick={onAccept}
                                disabled={!accepted || !hasScrolledToBottom}
                                className="flex-1 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                style={{
                                    background: accepted && hasScrolledToBottom
                                        ? 'linear-gradient(to right, var(--color-primary), var(--color-info))'
                                        : 'rgba(100, 116, 139, 0.3)',
                                    color: 'var(--color-text-inverse)',
                                    borderRadius: 'var(--radius-lg)',
                                    border: 'none',
                                    cursor: accepted && hasScrolledToBottom ? 'pointer' : 'not-allowed',
                                    transition: 'var(--transition-normal)',
                                }}
                                onMouseEnter={(e) => {
                                    if (accepted && hasScrolledToBottom) {
                                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 150, 255, 0.3)';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                <ShieldCheck size={16} />
                                Accept & Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        user_email: "",
        user_password: "",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [pendingLoginData, setPendingLoginData] = useState(null);

    const handleChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const proceedAfterLogin = async (loginRes) => {
        sessionStorage.setItem("authToken", loginRes.data?.response_data?.jwt_token || "");

        const profileRes = await userDetails();
        if (profileRes.success) {
            dispatch(setUserProfile(profileRes.data));
        }

        navigate("/dashboard");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const payload = {
            user_email: formData.user_email,
            user_password: formData.user_password,
        };

        const res = await login(payload);

        if (res.success) {
            // Check if this is the user's first login (disclaimer not yet accepted)
            const disclaimerKey = `disclaimer_accepted_${formData.user_email}`;
            const hasAcceptedDisclaimer = localStorage.getItem(disclaimerKey);

            if (!hasAcceptedDisclaimer) {
                // First-time login — show disclaimer before proceeding
                setPendingLoginData(res);
                setShowDisclaimer(true);
                setLoading(false);
            } else {
                // Returning user — proceed directly
                await proceedAfterLogin(res);
                setLoading(false);
            }
        } else {
            setError(res.error || "Login failed. Please try again.");
            setLoading(false);
        }
    };

    const handleDisclaimerAccept = async () => {
        setShowDisclaimer(false);
        setLoading(true);

        // Mark disclaimer as accepted for this user
        const disclaimerKey = `disclaimer_accepted_${formData.user_email}`;
        localStorage.setItem(disclaimerKey, new Date().toISOString());

        if (pendingLoginData) {
            await proceedAfterLogin(pendingLoginData);
            setPendingLoginData(null);
        }

        setLoading(false);
    };

    const handleDisclaimerDecline = () => {
        setShowDisclaimer(false);
        setPendingLoginData(null);
        // Clear the auth token since the user declined
        sessionStorage.removeItem("authToken");
        setError("You must accept the disclaimer to access stock recommendations.");
    };

    return (
        <>
            {loading && <CustomLoader />}

            {/* Disclaimer Popup */}
            {showDisclaimer && (
                <DisclaimerPopup
                    onAccept={handleDisclaimerAccept}
                    onDecline={handleDisclaimerDecline}
                />
            )}

            <div
                className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-primary)' }}
            >
                {/* Animated Candlestick Chart Background */}
                <CandlestickChartBackground />

                {/* Grid Pattern Overlay */}
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: 'linear-gradient(rgba(59,130,246,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(59,130,246,0.02) 1px,transparent 1px)',
                        backgroundSize: '50px 50px',
                    }}
                />

                {/* Login Box */}
                <div className="relative w-full max-w-md z-10">
                    <div
                        className="backdrop-blur-xl rounded-2xl p-8 space-y-6"
                        style={{
                            backgroundColor: 'var(--color-surface)',
                            boxShadow: 'var(--shadow-2xl)',
                            border: '1px solid var(--color-border-primary)',
                            borderRadius: 'var(--radius-2xl)',
                        }}
                    >
                        {/* Logo */}
                        <div className="text-center">
                            <h1
                                className="font-bold text-3xl mb-2 cursor-pointer"
                                style={{ color: 'var(--color-primary)' }}
                                onClick={() => navigate("/landingPage")}
                            >
                                FactorAlpha
                            </h1>
                            <div
                                className="h-1 w-20 mx-auto rounded-full"
                                style={{
                                    background: 'linear-gradient(to right, var(--color-primary), var(--color-info))',
                                    borderRadius: 'var(--radius-full)',
                                }}
                            />
                        </div>

                        {/* Heading */}
                        <div className="text-center">
                            <p
                                className="font-semibold text-2xl mb-2"
                                style={{ color: 'var(--color-text-primary)' }}
                            >
                                Member Login
                            </p>
                            <p
                                className="text-sm"
                                style={{ color: 'var(--color-text-secondary)' }}
                            >
                                Access real-time market data and premium analysis
                            </p>
                        </div>

                        {/* Error */}
                        {error && (
                            <div
                                className="p-3 rounded-lg text-sm"
                                style={{
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    color: 'var(--color-error)',
                                    border: '1px solid var(--color-error)',
                                    borderRadius: 'var(--radius-lg)',
                                    fontSize: 'var(--font-size-sm)',
                                }}
                            >
                                {error}
                            </div>
                        )}

                        {/* FORM */}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <input
                                name="user_email"
                                placeholder="Email"
                                value={formData.user_email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                                style={{
                                    backgroundColor: 'var(--color-surface)',
                                    border: '1px solid var(--color-border-primary)',
                                    borderRadius: 'var(--radius-lg)',
                                    color: 'var(--color-text-primary)',
                                    transition: 'var(--transition-normal)',
                                }}
                                onFocus={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--color-primary)';
                                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 150, 255, 0.2)';
                                }}
                                onBlur={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--color-border-primary)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            />
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="user_password"
                                    placeholder="Password"
                                    value={formData.user_password}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                                    style={{
                                        backgroundColor: 'var(--color-surface)',
                                        border: '1px solid var(--color-border-primary)',
                                        borderRadius: 'var(--radius-lg)',
                                        color: 'var(--color-text-primary)',
                                        transition: 'var(--transition-normal)',
                                    }}
                                    onFocus={(e) => {
                                        e.currentTarget.style.borderColor = 'var(--color-primary)';
                                        e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 150, 255, 0.2)';
                                    }}
                                    onBlur={(e) => {
                                        e.currentTarget.style.borderColor = 'var(--color-border-primary)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute inset-y-0 right-3 flex items-center transition-colors"
                                    style={{
                                        color: 'var(--color-text-muted)',
                                        transition: 'var(--transition-fast)',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-text-primary)'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-muted)'}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? (
                                        <Eye size={20} />
                                    ) : (
                                        <EyeOff size={20} />
                                    )}
                                </button>
                            </div>
                            <div className="flex justify-end">
                                <Link
                                    to="/forgotPassword"
                                    className="text-sm font-medium hover:underline transition-colors"
                                    style={{
                                        color: 'var(--color-primary)',
                                        fontSize: 'var(--font-size-sm)',
                                    }}
                                >
                                    Forgot password?
                                </Link>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    background: 'linear-gradient(to right, var(--color-primary), var(--color-info))',
                                    color: 'var(--color-text-inverse)',
                                    borderRadius: 'var(--radius-lg)',
                                    boxShadow: 'var(--shadow-lg)',
                                    transition: 'var(--transition-normal)',
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 150, 255, 0.3)';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                {loading && (
                                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                                )}
                                {loading ? "Logging in..." : "Login"}
                            </button>
                        </form>

                        {/* Footer */}
                        <div
                            className="text-center pt-4 border-t"
                            style={{
                                borderTopColor: 'var(--color-border-primary)',
                                fontSize: 'var(--font-size-sm)',
                            }}
                        >
                            <span style={{ color: 'var(--color-text-secondary)' }}>
                                Don&apos;t have an account?{" "}
                            </span>
                            <Link
                                to="/signup"
                                className="font-medium hover:underline transition-colors"
                                style={{
                                    color: 'var(--color-primary)',
                                    fontWeight: 'var(--font-weight-medium)',
                                }}
                            >
                                Create New Account
                            </Link>
                        </div>
                    </div>

                    {/* Decorative Elements */}
                    <div
                        className="absolute -top-4 -left-4 w-24 h-24 rounded-full blur-2xl"
                        style={{
                            backgroundColor: 'rgba(0, 150, 255, 0.1)',
                            borderRadius: 'var(--radius-full)',
                        }}
                    />
                    <div
                        className="absolute -bottom-4 -right-4 w-32 h-32 rounded-full blur-2xl"
                        style={{
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            borderRadius: 'var(--radius-full)',
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default Login;